import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ImageUploader from "@/components/admin/ImageUploader";

const COLORS = [
  ["primary_color", "Primary · text & buttons (Ink)", "#1a1a1a"],
  ["accent_color", "Accent · highlights (Warm)", "#d9cdb8"],
  ["background_color", "Background (Linen)", "#f4efe6"],
  ["card_color", "Cards / panels (Cream)", "#ebe3d5"],
];

const TEXT = [
  ["name", "Business name", "Sew Voyage"],
  ["tagline", "Tagline", "Conscious by choice. Luxurious by nature. Yours to voyage."],
  ["contact_email", "Contact email", "hello@brand.com"],
  ["contact_phone", "Contact phone", "+1 555 000 0000"],
  ["instagram", "Instagram URL", "https://instagram.com/…"],
  ["twitter", "Twitter / X URL", "https://x.com/…"],
  ["facebook", "Facebook URL", "https://facebook.com/…"],
];

export default function AdminBrand() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings/brand").then(({ data }) => setF(data || {})); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/brand", f); toast.success("Brand Kit saved — reload the storefront to see it live."); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  if (!f) return <p className="font-body text-sm text-ink/40">Loading…</p>;

  return (
    <div data-testid="admin-brand" className="max-w-2xl">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl">Brand Kit</h1>
          <p className="font-body text-sm text-ink/50 mt-1">One screen to rebrand the whole storefront — name, logo, colors and contact cascade everywhere.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/" target="_blank" className="adm-btn-outline" data-testid="brand-view"><ExternalLink size={13} /> View</Link>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-linen px-5 py-2.5 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink/90 disabled:opacity-50" data-testid="brand-save">
            <Save size={14} /> {saving ? "Saving…" : "Save Brand Kit"}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        <div className="adm-card space-y-4">
          <p className="font-heading text-xl">Identity</p>
          {TEXT.map(([k, label, ph]) => (
            <div key={k}>
              <label className="adm-label">{label}</label>
              <input className="adm-input" value={f[k] || ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`brand-${k}`} />
            </div>
          ))}
        </div>

        <div className="adm-card space-y-5">
          <div>
            <p className="font-heading text-xl">Logos</p>
            <p className="font-body text-[0.7rem] text-ink/50 mt-1">Upload a file or paste an image URL for each. Leave the header/footer/hero overrides empty to use the Primary logo everywhere. Leave Primary empty to fall back to the business-name wordmark.</p>
          </div>
          <ImageUploader value={f.logo} onChange={(url) => set("logo", url)} label="Primary logo · used everywhere by default" fit="contain" recommended="Transparent PNG · wide ~400×110 or square ~300×300" testId="brand-logo-uploader" />
          <div className="border-t hairline pt-5 space-y-5">
            <p className="font-body text-[0.6rem] uppercase tracking-wide2 text-ink/40">Per-location overrides (optional)</p>
            <ImageUploader value={f.header_logo} onChange={(url) => set("header_logo", url)} label="Header logo" fit="contain" recommended="Transparent PNG · height ~44px, e.g. 320×88" testId="brand-header-logo-uploader" />
            <ImageUploader value={f.footer_logo} onChange={(url) => set("footer_logo", url)} label="Footer logo" fit="contain" recommended="Transparent PNG · height ~80px, e.g. 400×110" testId="brand-footer-logo-uploader" />
            <ImageUploader value={f.hero_logo} onChange={(url) => set("hero_logo", url)} label="Homepage hero logo (above “Resort Wear, Reimagined”)" fit="contain" recommended="Transparent PNG · height ~112px, e.g. 500×220" testId="brand-hero-logo-uploader" />
          </div>
          <p className="font-body text-[0.7rem] text-ink/50 border-t hairline pt-4">The decorative sketch mark (footer & hero side branches) is editable in <Link to="/admin/page-images" className="underline">Page Images → brand mark</Link>.</p>
        </div>

        <div className="adm-card space-y-4">
          <p className="font-heading text-xl">Colors</p>
          <p className="font-body text-[0.7rem] text-ink/50">These cascade site-wide instantly on the next storefront load.</p>
          {COLORS.map(([k, label, def]) => (
            <div key={k} className="flex items-center gap-3">
              <input type="color" value={f[k] || def} onChange={(e) => set(k, e.target.value)} className="w-10 h-10 rounded border border-ink/20 bg-transparent cursor-pointer" data-testid={`brand-${k}-picker`} />
              <input className="adm-input w-32 font-mono" value={f[k] || def} onChange={(e) => set(k, e.target.value)} data-testid={`brand-${k}`} />
              <span className="font-body text-sm text-ink/70">{label}</span>
            </div>
          ))}
          <button onClick={() => setF((s) => ({ ...s, primary_color: "", accent_color: "", background_color: "", card_color: "" }))} className="font-body text-[0.6rem] uppercase tracking-wide2 text-ink/50 hover:text-ink" data-testid="brand-reset-colors">Reset to defaults</button>
        </div>

        <p className="font-body text-[0.7rem] text-ink/50">Payment handles (Cash App, Venmo, PayPal) live in <Link to="/admin/settings" className="underline">Settings → Payments</Link>.</p>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-linen px-6 py-3 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink/90 disabled:opacity-50" data-testid="brand-save-bottom">
            <Save size={14} /> {saving ? "Saving…" : "Save Brand Kit"}
          </button>
        </div>
      </div>
    </div>
  );
}
